/**
 * NOTE (Ryan, 2026-02-13): IntegrationStatusToolBlock
 *
 * Renders the IntegrationStatusCard directly from the MCP tool call/result,
 * instead of relying on the model to echo <integration-status> XML tags.
 * This makes the card deterministic — it renders whenever the tool is called,
 * regardless of what the model says in its text response.
 */

import type { IntegrationScope } from "schemas/generated/agentIntegration";
import type { OpencodeToolPart } from "schemas/generated/opencode";

import React from "react";

import { IntegrationStatusCard } from "@agent-ui/features/chat/components/chat-elements/chat-message/IntegrationStatusCard";
import { parseJsonOrNull } from "replo-utils/lib/json";

type IntegrationStatusPayload = {
  integrationKey: string;
  name: string;
  description: string;
  isConnected: boolean;
  connectionId?: string;
  scope: IntegrationScope;
};

function parseIntegrationScope(scopeValue: unknown): IntegrationScope | null {
  if (
    typeof scopeValue !== "object" ||
    scopeValue === null ||
    Array.isArray(scopeValue)
  ) {
    return null;
  }

  const scopeRecord = scopeValue as Record<string, unknown>;
  if (
    (scopeRecord.user !== undefined && scopeRecord.user !== true) ||
    (scopeRecord.project !== undefined && scopeRecord.project !== true) ||
    (scopeRecord.workspace !== undefined && scopeRecord.workspace !== true)
  ) {
    return null;
  }

  const parsedScope: IntegrationScope = {};
  if (scopeRecord.user === true) {
    parsedScope.user = true;
  }
  if (scopeRecord.project === true) {
    parsedScope.project = true;
  }
  if (scopeRecord.workspace === true) {
    parsedScope.workspace = true;
  }

  if (
    parsedScope.user !== true &&
    parsedScope.project !== true &&
    parsedScope.workspace !== true
  ) {
    return null;
  }

  return parsedScope;
}

function parseIntegrationStatusPayload(
  integrationStatusValue: unknown,
): IntegrationStatusPayload | null {
  if (
    typeof integrationStatusValue !== "object" ||
    integrationStatusValue === null ||
    Array.isArray(integrationStatusValue)
  ) {
    return null;
  }

  const integrationStatusRecord = integrationStatusValue as Record<
    string,
    unknown
  >;
  if (
    typeof integrationStatusRecord.integrationKey !== "string" ||
    typeof integrationStatusRecord.name !== "string" ||
    typeof integrationStatusRecord.description !== "string" ||
    typeof integrationStatusRecord.isConnected !== "boolean"
  ) {
    return null;
  }

  if (
    integrationStatusRecord.connectionId !== undefined &&
    typeof integrationStatusRecord.connectionId !== "string"
  ) {
    return null;
  }

  const parsedScope = parseIntegrationScope(integrationStatusRecord.scope);
  if (!parsedScope) {
    return null;
  }

  return {
    integrationKey: integrationStatusRecord.integrationKey,
    name: integrationStatusRecord.name,
    description: integrationStatusRecord.description,
    isConnected: integrationStatusRecord.isConnected,
    connectionId: integrationStatusRecord.connectionId,
    scope: parsedScope,
  };
}

/**
 * Extracts integration status from an opencode tool part's output.
 * Supports direct status objects and MCP content wrappers.
 */
function extractIntegrationStatusFromOutput(
  output: string,
): IntegrationStatusPayload | null {
  const parsed = parseJsonOrNull(output);
  if (!parsed) {
    return null;
  }

  const directIntegrationStatus = parseIntegrationStatusPayload(parsed);
  if (directIntegrationStatus) {
    return directIntegrationStatus;
  }

  // MCP content array format — [{ type: "text", text: "..." }]
  if (Array.isArray(parsed) && parsed.length > 0) {
    const firstItem = parsed[0] as Record<string, unknown> | undefined;
    if (firstItem?.type === "text" && typeof firstItem.text === "string") {
      const innerParsed = parseJsonOrNull(firstItem.text);
      const innerIntegrationStatus = parseIntegrationStatusPayload(innerParsed);
      if (innerIntegrationStatus) {
        return innerIntegrationStatus;
      }
    }
  }

  // MCP content object format — { content: [{ type: "text", text: "..." }] }
  if (
    typeof parsed === "object" &&
    !Array.isArray(parsed) &&
    "content" in parsed
  ) {
    const content = (parsed as Record<string, unknown>).content;
    if (Array.isArray(content) && content.length > 0) {
      const firstItem = content[0] as Record<string, unknown> | undefined;
      if (firstItem?.type === "text" && typeof firstItem.text === "string") {
        const innerParsed = parseJsonOrNull(firstItem.text);
        const innerIntegrationStatus =
          parseIntegrationStatusPayload(innerParsed);
        if (innerIntegrationStatus) {
          return innerIntegrationStatus;
        }
      }
    }
  }

  return null;
}

/**
 * Renders the IntegrationStatusCard from an opencode tool part.
 * This is the adapter for the new opencode-based message system.
 */
export function OpencodeIntegrationStatusToolBlock({
  toolPart,
}: {
  toolPart: OpencodeToolPart;
}) {
  const integrationKey = (
    toolPart.state.input as Record<string, unknown> | undefined
  )?.integrationKey as string | undefined;

  if (toolPart.state.status === "completed") {
    const integrationStatus = extractIntegrationStatusFromOutput(
      toolPart.state.output,
    );
    if (integrationStatus) {
      return (
        <IntegrationStatusCard
          integrationKey={integrationStatus.integrationKey}
          name={integrationStatus.name}
          description={integrationStatus.description}
          isConnected={integrationStatus.isConnected}
          connectionId={integrationStatus.connectionId}
        />
      );
    }

    return (
      <div className="text-sm text-muted">
        {integrationKey
          ? `Could not load status for ${integrationKey}`
          : "Could not load integration status"}
      </div>
    );
  }

  if (toolPart.state.status === "error") {
    return (
      <div className="text-sm text-muted">
        {integrationKey
          ? `Failed to load status for ${integrationKey}`
          : "Failed to load integration status"}
      </div>
    );
  }

  return (
    <div className="text-sm text-muted">
      {integrationKey
        ? `Loading ${integrationKey} status...`
        : "Loading integration status..."}
    </div>
  );
}
